// src/components/PortfolioSidebar/portfolio_sidebar.js

import SidebarShell from "@/components/SidebarShell/SidebarShell.vue";
import SidebarContext from "@/components/SidebarContext/SidebarContext.vue";
import SidebarUserFooter from "@/components/SidebarUserFooter/SidebarUserFooter.vue";
import brand_details from "@/data/brand_details";
import { SIDEBAR_CONFIG, ROLE_TO_APP_KEY } from "@/data/sidebar_config";
import { role_dashboards } from "@/config/dashboardIds";

// IMPORT UTILITIES
import { get_user_profile } from "@/router";

const details = brand_details();

export default {
  name: "PortfolioSidebar",

  components: ({
    SidebarShell,
    SidebarContext,
    SidebarUserFooter
  }),

  props: {
    tiles: {
      type: Array,
      default: () => []
    },
    current_active_property: {
      type: String,
      default: null
    },

    /**
     * Optional override from parent.
     * fund_manager | donor_portal | governance | entrepreneur
     */
    app_key: ({
      type: String,
      required: false,
      default: null
    })
  },

  data() {
    return ({
      // Brand Information
      brand_name: details.name,
      brand_subtitle: details.subtitle,
      entity_name: details.sub_entity_name,

      // Context State
      selected_portfolio: details.sub_entity_name,
      local_active_property: null,

      // ✅ default active
      active_menu_item: "executive-overview",

      // optional collapsed props list
      show_collapsed_properties: false,

      // ✅ accordion state
      governance_open: false,
      impact_engine_open: false,

      // Footer Data
      full_name: "Guest User",
      initials: "GU",

      // Brand Logo
      short_logo: details.logo,

      // ✅ resolved sidebar config key
      resolved_app_key: "fund_manager"
    });
  },

  computed: {
    sidebar_config() {
      return SIDEBAR_CONFIG[this.resolved_app_key] || SIDEBAR_CONFIG.fund_manager;
    },

    /**
     * ✅ menuItems now comes from sidebar_config.js
     */
    menuItems() {
      return this.sidebar_config.menu || [];
    },

    role_dashboard_map() {
      const app_to_role_key = {
        fund_manager: "fund_manager",
        donor_portal: "donor",
        governance: "governance",
        entrepreneur: "entrepreneur"
      };

      const role_key = app_to_role_key[this.resolved_app_key] || this.resolved_app_key;
      return role_dashboards[String(role_key)] || {};
    },

    // ✅ highlight accordion header when one of its children is active
    is_governance_active() {
      const governance = (this.menuItems || []).find(i => i.key === "governance-risk-audit");
      const child_keys = (governance?.children || []).map(c => c.key);
      return child_keys.includes(this.active_menu_item);
    },

    is_impact_engine_active() {
      const engine = (this.menuItems || []).find(i => i.key === "impact-exchange-rate-engine");
      const child_keys = (engine?.children || []).map(c => c.key);
      return child_keys.includes(this.active_menu_item);
    }
  },

  mounted() {
    this.load_user_name();
    this.resolve_sidebar_from_role();

    if (this.current_active_property) {
      this.local_active_property = this.current_active_property;
      this.selected_portfolio = this.current_active_property;
    }

    // ✅ auto-open accordion if active item is a child
    if (this.is_governance_child(this.active_menu_item)) {
      this.governance_open = true;
    }

    if (this.is_impact_engine_child(this.active_menu_item)) {
      this.impact_engine_open = true;
    }

    // ✅ if current active menu not in this sidebar, default to first menu item
    this.ensure_active_menu_valid();
  },

  methods: {

    resolve_sidebar_from_role() {
      const explicit_key = this._get_explicit_app_key();
      if (explicit_key) {
        this.resolved_app_key = explicit_key;
        return;
      }

      const raw_role = this._get_user_role_raw();
      const normalized_role = this._normalize_role(raw_role);
      this.resolved_app_key = this._resolve_app_key_from_role(normalized_role);
    },

    _get_explicit_app_key() {
      const key = this.app_key;
      if (!key) return null;
      return SIDEBAR_CONFIG[String(key)] ? key : null;
    },

    _get_user_role_raw() {
      const user = get_user_profile() || {};

      const role_candidates = [
        user.role_key,
        user.role,
        user.user_role?.role?.name,
        user.user_role?.role_name,
        user.profile_role,
        sessionStorage.getItem("role"),
      ];

      for (const value of role_candidates) {
        if (value !== null && value !== undefined && String(value).trim() !== "") {
          return String(value).trim().toLowerCase();
        }
      }

      return "";
    },

    _normalize_role(raw_role) {
      return String(raw_role || "").replace(/\s+/g, "_");
    },

    _resolve_app_key_from_role(normalized_role) {
      return ROLE_TO_APP_KEY[String(normalized_role)] || "fund_manager";
    },

    /** ✅ make sure active menu exists in current menu list */
    ensure_active_menu_valid() {
      const flat_keys = [];

      (this.menuItems || []).forEach(item => {
        if (item.type === "accordion") {
          (item.children || [])
            .filter(child => this.has_dashboard_id(child.key))
            .forEach(child => {
              flat_keys.push(child.key);
            });

          if (this.has_dashboard_id(item.key)) {
            flat_keys.push(item.key);
          }
        } else if (item.type !== "component") {
          if (this.has_dashboard_id(item.key)) {
            flat_keys.push(item.key);
          }
        }
      });

      if (!flat_keys.includes(this.active_menu_item)) {
        // pick first real item
        const first = (this.menuItems || []).find(i => i.type !== "component" && this.has_dashboard_id(i.key));
        if (first?.type === "accordion") {
          this.active_menu_item = first.children?.find(child => this.has_dashboard_id(child.key))?.key || first.key;
        } else {
          this.active_menu_item = first?.key || null;
        }
      }
    },

    toggle_impact_engine() {
      this.impact_engine_open = !this.impact_engine_open;
    },

    /** FOOTER NAME / INITIALS */
    load_user_name() {
      const user = get_user_profile();
      let name = "Guest User";

      if (user && (user.first_name || user.last_name)) {
        name = `${user.first_name || ""} ${user.last_name || ""}`.trim();
      } else {
        const first = sessionStorage.getItem("first_name") || "";
        const last = sessionStorage.getItem("last_name") || "";
        name = `${first} ${last}`.trim() || "Guest User";
      }

      if (name === "Guest User" && user?.email) {
        name = user.email.split("@")[0];
      }

      const parts = name.split(" ");
      const initials = ((parts[0]?.[0] || "") + (parts[1]?.[0] || "")).toUpperCase();

      this.full_name = name;
      this.initials = initials || "GU";
    },

    toggleCollapsedProperties() {
      this.show_collapsed_properties = !this.show_collapsed_properties;
    },

    /** ✅ Accordion helpers */
    toggle_governance() {
      this.governance_open = !this.governance_open;
    },

    is_governance_child(key) {
      const governance = (this.menuItems || []).find(i => i.key === "governance-risk-audit");
      return (governance?.children || []).some(c => c.key === key);
    },

    is_impact_engine_child(key) {
      const engine = (this.menuItems || []).find(i => i.key === "impact-exchange-rate-engine");
      return (engine?.children || []).some(c => c.key === key);
    },

    /** ✅ MENU SELECTION */
    has_dashboard_id(key) {
      const dashboard_id = this.role_dashboard_map[String(key)];
      return Boolean(dashboard_id) && !String(dashboard_id).startsWith("REPLACE_");
    },

    select_menu_item(menuItem) {
      if (!this.has_dashboard_id(menuItem)) return;

      this.active_menu_item = menuItem;
      this.dropdownOpen = false;

      // ✅ if child selected, ensure accordion stays open
      if (this.is_governance_child(menuItem)) {
        this.governance_open = true;
      }

      if (this.is_impact_engine_child(menuItem)) {
        this.impact_engine_open = true;
      }

      // ✅ If no property selected, emit global menu selection
      if (!this.local_active_property) {
        this.$emit("global-menu-selected", menuItem);
        return;
      }

      // ✅ If property selected, emit property menu selection
      const tile = (this.tiles || []).find(t => t.title === this.local_active_property);

      if (tile) {
        this.$emit("property-menu-selected", {
          menuItem,
          propertyId: tile.id,
          propertyName: tile.title
        });
      } else {
        this.$emit("global-menu-selected", menuItem);
      }
    },

    /** OPTIONAL: keep these if you use them in your parent */
    select_global_portfolio() {
      this.selected_portfolio = "Global Portfolio";
      this.local_active_property = null;
      this.active_menu_item = "executive-overview";
      this.dropdownOpen = false;

      this.$emit("global-selected");
      this.$emit("global-menu-selected", "executive-overview");
    },

    select_tile_directly(tile) {
      this.selected_portfolio = tile.label;
      this.local_active_property = tile.label;
      this.active_menu_item = "executive-overview";

      this.$emit("tile-clicked", tile.id);

      this.$emit("property-menu-selected", {
        menuItem: "executive-overview",
        propertyId: tile.id,
        propertyName: tile.label
      });
    }
  }
};
